import React, { useState } from "react";
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import WorkIcon from "@mui/icons-material/Work";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import { Contact } from "../../../../interfaces/DTO/crm/contact";
import EmploymentRecordCard, { EmploymentRecord, EmploymentRecordKind } from "./EmploymentRecordCard";
import { useContactEmploymentSave } from "./useContactEmploymentSave";

interface ContactEmploymentSectionProps {
  contact: Contact;
}

const ADD_OPTIONS: { kind: EmploymentRecordKind; label: string; icon: React.ReactNode }[] = [
  { kind: "employment", label: "כשכיר", icon: <WorkIcon fontSize="small" /> },
  { kind: "business", label: "כעצמאי", icon: <StorefrontIcon fontSize="small" /> },
  { kind: "company", label: "כעסק (ח.פ חברה)", icon: <BusinessIcon fontSize="small" /> },
];

/**
 * תעסוקה אחרונה – מקומות עבודה כשכיר (LastEmploymentData), עסקים כעצמאי (BizData) וח.פ חברה (בעל שליטה) ככרטיסים.
 * אפשר להוסיף כל סוג בכל סטטוס תעסוקתי – זו התעסוקה האחרונה, גם אם הלקוח כבר לא עובד.
 */
const ContactEmploymentSection: React.FC<ContactEmploymentSectionProps> = ({ contact }) => {
  const save = useContactEmploymentSave(contact);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [adding, setAdding] = useState<EmploymentRecordKind | null>(null);

  const employments = contact.data?.last_employment_data ?? [];
  const businesses = contact.data?.biz_data ?? [];
  const companyId = contact.primary_biz_id?.trim() ?? "";

  const isEmpty = employments.length === 0 && businesses.length === 0 && !companyId && !adding;

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">תעסוקה אחרונה</Typography>
        <Button
          size="small"
          color="secondary"
          startIcon={<AddIcon sx={{ ml: 1 }} />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={!!adding}
        >
          הוסף מקום עבודה
        </Button>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} dir="rtl">
          {ADD_OPTIONS.filter((o) => o.kind !== "company" || !companyId).map((o) => (
            <MenuItem
              key={o.kind}
              onClick={() => {
                setMenuAnchor(null);
                setAdding(o.kind);
              }}
            >
              <ListItemIcon>{o.icon}</ListItemIcon>
              <ListItemText>{o.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {companyId && (
        <EmploymentRecordCard
          kind="company"
          value={{ primary_biz_id: companyId }}
          onSave={(r) => save.saveCompanyId(String(r.primary_biz_id ?? ""))}
          onDelete={() => save.saveCompanyId("")}
        />
      )}
      {employments.map((employment, index) => (
        <EmploymentRecordCard
          key={`employment-${index}`}
          kind="employment"
          value={employment as EmploymentRecord}
          onSave={(r) => save.updateEmployment(index, r)}
          onDelete={() => save.deleteEmployment(index)}
        />
      ))}
      {businesses.map((business, index) => (
        <EmploymentRecordCard
          key={`business-${index}`}
          kind="business"
          value={business as EmploymentRecord}
          onSave={(r) => save.updateBusiness(index, r)}
          onDelete={() => save.deleteBusiness(index)}
        />
      ))}
      {adding && (
        <EmploymentRecordCard
          key={`new-${adding}`}
          kind={adding}
          value={null}
          onSave={async (r) => {
            await save.add(adding, r);
            setAdding(null);
          }}
          onCancelNew={() => setAdding(null)}
        />
      )}

      {isEmpty && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 1 }}>
          לא הוזנה תעסוקה
        </Typography>
      )}
    </Box>
  );
};

export default ContactEmploymentSection;
